import { HttpClient } from "@angular/common/http";
import { Observable, catchError, combineLatest, from, lastValueFrom, map, of, Subject, switchMap, race, timer } from "rxjs";
import { Injectable } from "@angular/core";
import { getOptionsQueryParams } from "@app/class/Utilities";
import { IDriverFilter } from "@app/interfaces/filters/IDriverFilter";
import { IGroupFilter } from "@app/interfaces/filters/IGroupFilter";
import { IVehicleFilter } from "@app/interfaces/filters/IVehicleFilter";
import { IDriver } from "@app/interfaces/IDriver";
import { IUser } from "@app/interfaces/IUser";
import { IVehicle } from "@app/interfaces/IVehicle";
import { IVehicleDriverMapping } from "@app/interfaces/IVehicleDriverMapping";
import { ConstantsService } from "@app/services/constantsService";
import { CurrentUserService } from "@app/services/currentUser.service";
import { DateService, DateUnit } from "@app/services/date.service";
import { DriverService } from "@app/services/driver.service";
import { DriverAssignmentService } from "@app/services/driverAssignment.service";
import { PlotService } from "@app/services/plots/plot.service";
import { TranslationService } from "@app/services/translation.service";
import { UnitConversionService } from "@app/services/unitConversion.service";
import { UsersService } from "@app/services/users.service";
import { VehicleService } from "@app/services/vehicle.service";
import { VideoCoachingStatus } from "../enums/VideoCoachingStatus.enum";
import { VideoEventCode } from "../enums/VideoEventCode";
import { VideoObservation } from "../enums/VideoObservation";
import { VideoReviewStatus } from "../enums/VideoReviewStatus.enum";
import { VideoReviewSubStatus } from "../enums/VideoReviewSubStatus.enum";
import { VideoStatus } from "../enums/VideoStatus";
import { IGetVideoEntryOptions } from "../models/IGetVideoEntryOptions";
import { IUserAction } from "../models/IUserAction";
import { IVideoChannelMedia } from "../models/IVideoChannelMedia";
import { IVideoEntry } from "../models/IVideoEntry";
import { IVideoExtensionData } from "../models/IVideoExtensionData";
import { IVideoRequest } from "../models/IVideoRequest";
import { IVideoThumbnail } from "../models/IVideoThumbnail";
import { CameraStatusService } from "./camera-status.service";
import { VideoReviewStatusService } from "./video-review-status.service";
import { DataCacheService } from "@app/services/caching/data-cache.service";

@Injectable({
    providedIn: 'root'
})
export class VideoEntryService {
    private downloadVideoCountUpdatedSubject = new Subject<void>();
    downloadVideoCountUpdated$ = this.downloadVideoCountUpdatedSubject.asObservable();

    private videoUpdateSubject = new Subject<IVideoEntry>();
    videoUpdate$ = this.videoUpdateSubject.asObservable();

    private apiUrl: string;

    private activeTimePeriodCaches = new Set<string>();

    private recentlyModifiedVideos = new Map<number, IVideoEntry>();

    private videosEntriesCachePrefix = "videosEntries";
    private videosRequestedCachePrefix = "videosRequested";
    private channelMediaCachePrefix = "channelMedia";

    private vehiclesCacheName = "vehicles";
    private driversCacheName = "drivers";
    private driverAssignmentsCacheName = "driverAssignments";
    private currentUserCacheName = "currentUser";
    private usersCacheName = "users";

    constructor(
        private readonly httpClient: HttpClient,
        private readonly constantsService: ConstantsService,
        private readonly dateService: DateService,
        private readonly vehicleService: VehicleService,
        private readonly translationService: TranslationService,
        private readonly unitConversionService: UnitConversionService,
        private readonly driverService: DriverService,
        private readonly driverAssignmentService: DriverAssignmentService,
        private readonly currentUserService: CurrentUserService,
        private readonly usersService: UsersService,
        private readonly videoReviewStatusService: VideoReviewStatusService,
        private readonly cameraStatusService: CameraStatusService,
        private readonly plotService: PlotService,
        private readonly dataCacheService: DataCacheService
    ) {
        this.apiUrl = this.constantsService.getApiUrl();
    }

    getVideosEntriesWithMappings$(startTime: Date, endTime: Date, options?: IGetVideoEntryOptions): Observable<IVideoEntry[]> {
        // Get videos from cache or direct API
        return this.getVideosEntries$(startTime, endTime, options?.invalidateCache).pipe(
            switchMap(videos => {
                // Process videos
                let processedVideos = videos;

                if (processedVideos.length > 0 && options?.processEntries) {
                    processedVideos = processedVideos
                        .map(v => this.getVideoEntryForDisplay(v))
                        .map(v => this.getVideoWithMetadataFormattedForDisplay(v, null));
                }

                if (processedVideos.length > 0 && (options?.processEntries || options?.createFileUrls)) {
                    processedVideos = this.getVideoEntriesWithFileUrls(processedVideos);
                }

                if (!options?.includeArchived) {
                    processedVideos = processedVideos.filter(v => v.Status != VideoStatus.Archived);
                }

                if (options?.downloadedOnly) {
                    processedVideos = processedVideos.filter(v => v.Status == VideoStatus.Downloaded || v.Status == VideoStatus.PartiallyDownloaded);
                }

                processedVideos = this.getVideosFiltered(processedVideos, options?.vehicleFilter, options?.driverFilter, options?.groupFilter);

                // Get all related data
                return combineLatest([
                    of(processedVideos),
                    this.getAllChannelVideos$(startTime, endTime, !!options?.invalidateCache),
                    from(this.vehicleService.getVehiclesBasic({ HasInstalledDevice: false }) as Promise<IVehicle[]>),
                    this.getDrivers$(options?.invalidateCache),
                    this.getDriverAssignments$(options?.invalidateCache),
                    this.getCurrentUser$(options?.invalidateCache),
                    this.getUsers$(options?.invalidateCache)
                ]);
            }),
            map(([videos, channelMedia, vehicles, drivers, driverAssignments, currentUser, users]) => {
                return this.mapVideoEntries(videos, channelMedia, vehicles, drivers, driverAssignments, currentUser, users);
            }),
            catchError(error => {
                console.error('[VideoEntryService] Error in getVideosEntriesWithMappings$:', error);
                return of([]);
            })
        );
    }

    private getVideosDirectly$(startTime: Date, endTime: Date): Observable<IVideoEntry[]> {
        const options = getOptionsQueryParams({ startTime, endTime });
        return this.httpClient.get<IVideoEntry[]>("/api/cameras/entries", options).pipe(
            catchError(error => {
                console.error('[VideoEntryService] Error fetching videos directly:', error);
                return of([]);
            })
        );
    }

    getVideoEntriesRequestedWithMappings$(options: IGetVideoEntryOptions = {}): Observable<IVideoEntry[]> {
        if (!options?.startTime || !options?.endTime) {
            const now = new Date();
            options = {
                ...options,
                endTime: now,
                startTime: this.dateService.addOrSubtractTime(now, -1, DateUnit.Month)
            };
        }

        return this.getVideoEntriesRequested$(options).pipe(
            switchMap(videos => {
                return combineLatest([
                    of(videos),
                    this.getAllChannelVideos$(options.startTime, options.endTime, options.invalidateCache, true),
                    from(this.vehicleService.getVehiclesBasic({ HasInstalledDevice: false }) as Promise<IVehicle[]>),
                    this.getDrivers$(options.invalidateCache),
                    this.getDriverAssignments$(options.invalidateCache),
                    this.getCurrentUser$(options.invalidateCache),
                    this.getUsers$(options.invalidateCache)
                ]);
            }),
            map(([videos, channelMedia, vehicles, drivers, driverAssignments, currentUser, users]) => {
                let filteredVideos = videos;
                if (options.excludeMediaDeleted) {
                    filteredVideos = videos.filter(video => video.Status !== VideoStatus.MediaDeleted);
                }

                return this.mapVideoEntries(filteredVideos, channelMedia, vehicles, drivers, driverAssignments, currentUser, users);
            }),
            catchError(error => {
                console.error('[VideoEntryService] Error in getVideoEntriesRequestedWithMappings$:', error);
                return of([]);
            })
        );
    }

    private getVideosEntries$(startTime: Date, endTime: Date, invalidateCache: boolean = false): Observable<IVideoEntry[]> {
        const cacheKey = this.getTimeBasedCacheKey(this.videosEntriesCachePrefix, startTime, endTime);
        this.trackCache(cacheKey);

        const cacheRequest$ = this.dataCacheService.getOrRequest(
            cacheKey,
            () => this.httpClient.get<IVideoEntry[]>("/api/cameras/entries", getOptionsQueryParams({ startTime, endTime })),
            { invalidateCache, age: 300 }
        );

        const directRequest$ = this.getVideosDirectly$(startTime, endTime);

        return race(
            cacheRequest$,
            timer(5000).pipe(
                switchMap(() => {
                    return directRequest$;
                })
            )
        ).pipe(
            catchError(error => {
                console.error(`Error in getVideosEntries$ for ${cacheKey}:`, error);
                return directRequest$; // Fall back to direct request on any error
            })
        );
    }

    getVideoEntriesRequested$(options?: IGetVideoEntryOptions): Observable<IVideoEntry[]> {
        if (!options?.startTime || !options?.endTime) {
            const now = new Date();
            options = {
                ...options,
                endTime: now,
                startTime: this.dateService.addOrSubtractTime(now, -1, DateUnit.Month)
            };
        }

        const cacheKey = this.getTimeBasedCacheKey(this.videosRequestedCachePrefix, options.startTime, options.endTime);

        this.trackCache(cacheKey);

        return this.dataCacheService.getOrRequest(
            cacheKey,
            () => this.httpClient.get<IVideoEntry[]>("/api/cameras/entries/requested",
                getOptionsQueryParams({ startTime: options.startTime, endTime: options.endTime })),
            { invalidateCache: options.invalidateCache, age: 300 }
        );
    }

    getAllVideosEntries$(startTime: Date, endTime: Date, options?: IGetVideoEntryOptions): Observable<IVideoEntry[]> {
        return this.getVideosEntries$(startTime, endTime, options?.invalidateCache).pipe(
            map(videos => {
                // Process videos
                let processedVideos = videos;

                if (!options?.includeArchived) {
                    processedVideos = processedVideos.filter(v => v.Status != VideoStatus.Archived);
                }

                if (options?.downloadedOnly) {
                    processedVideos = processedVideos.filter(v => v.Status == VideoStatus.Downloaded || v.Status == VideoStatus.PartiallyDownloaded);
                }

                if (options?.processEntries) {
                    processedVideos = processedVideos
                        .map(v => this.getVideoEntryForDisplay(v))
                        .map(v => this.getVideoWithMetadataFormattedForDisplay(v, null));
                }

                if (options?.processEntries || options?.createFileUrls) {
                    processedVideos = this.getVideoEntriesWithFileUrls(processedVideos);
                }

                return this.getVideosFiltered(processedVideos, options?.vehicleFilter, options?.driverFilter, options?.groupFilter);
            }),
            catchError(error => {
                console.error('[VideoEntryService] Error in getAllVideosEntries$:', error);
                return of([]);
            })
        );
    }

    private getAllChannelVideos$(startTime: Date, endTime: Date, invalidateCache: boolean = false, useRequestedTime: boolean = false): Observable<IVideoChannelMedia[]> {
        const cacheKey = this.getTimeBasedCacheKey(this.channelMediaCachePrefix, startTime, endTime);

        this.trackCache(cacheKey);

        return this.dataCacheService.getOrRequest(
            cacheKey,
            () => this.httpClient.get<IVideoChannelMedia[]>("/api/cameras/channels/videos",
                getOptionsQueryParams({ startTime, endTime, useRequestedTime })),
            { invalidateCache, age: 300 }
        ).pipe(
            map(channels => this.getVideoChannelMediaForDisplay(channels))
        );
    }

    private getVehicles$(invalidateCache: boolean = false): Observable<IVehicle[]> {
        return this.dataCacheService.getOrRequest(
            this.vehiclesCacheName,
            () => from(this.vehicleService.getVehicles({
                includePeripheralMappings: true,
                includeAllVehicles: true
            }) as Promise<IVehicle[]>),
            { invalidateCache, age: 600 }
        );
    }

    private getDrivers$(invalidateCache: boolean = false): Observable<IDriver[]> {
        return this.dataCacheService.getOrRequest(
            this.driversCacheName,
            () => from(this.driverService.getDrivers() as Promise<IDriver[]>),
            { invalidateCache, age: 600 }
        );
    }

    private getDriverAssignments$(invalidateCache: boolean = false): Observable<IVehicleDriverMapping[]> {
        return this.dataCacheService.getOrRequest(
            this.driverAssignmentsCacheName,
            () => from(this.driverAssignmentService.getDriverAssignments(false) as Promise<IVehicleDriverMapping[]>),
            { invalidateCache, age: 600 }
        );
    }

    private getCurrentUser$(invalidateCache: boolean = false): Observable<IUser> {
        return this.dataCacheService.getOrRequest(
            this.currentUserCacheName,
            () => {
                return new Observable<IUser[]>(observer => {
                    const userPromise = this.currentUserService.getCurrentUser() as Promise<IUser>;
                    userPromise.then(user => {
                        observer.next([user]);
                        observer.complete();
                    }).catch(err => {
                        observer.error(err);
                    });
                });
            },
            { invalidateCache, age: 900 }
        ).pipe(
            map(users => users[0])
        );
    }

    private getUsers$(invalidateCache: boolean = false): Observable<IUser[]> {
        return this.dataCacheService.getOrRequest(
            this.usersCacheName,
            () => from(this.usersService.getUsers({ includeAdmins: true }) as Promise<IUser[]>),
            { invalidateCache, age: 600 }
        );
    }

    updateVideoEntry$(videoEntry: IVideoEntry): Observable<IVideoEntry> {
        const videoEntryCopy: IVideoEntry = {
            ...videoEntry,
            vehicle: null,
            driver: null,
            eventCategory: null,
            markerModel: null
        };

        return this.httpClient.post<IVideoEntry>("/api/cameras", videoEntryCopy).pipe(
            map(apiResult => {
                const result = { ...(videoEntry as any), ...(apiResult as any) } as IVideoEntry;

                this.updateVideoInAllCaches(result); // Update all caches when a video entry is updated

                this.downloadVideoCountUpdatedSubject.next();
                this.videoUpdateSubject.next(result);

                return result;
            }),
            catchError(error => {
                console.error('[VideoEntryService] Error updating video entry:', error);
                throw error;
            })
        );
    }

    getVideoWithLatestStatus(videos: IVideoEntry[]): IVideoEntry[] {
        if (!videos || videos.length === 0) {
            return videos;
        }

        return videos.map(video => {
            const recentVersion = this.recentlyModifiedVideos.get(video.Id);
            if (recentVersion) {
                return { ...video, Status: recentVersion.Status };
            }
            return video;
        });
    }

    toggleArchiveStatus(videoEntry: IVideoEntry, archive: boolean): Observable<IVideoEntry> {
        const updatedVideoEvent = {
            ...videoEntry,
            Status: archive ? VideoStatus.Archived :
                (videoEntry.Mp4VideoFileGuid ? VideoStatus.Downloaded : VideoStatus.EventOnly)
        };

        return this.updateVideoEntry$(updatedVideoEvent);
    }

    async getExtensionData(videoEntry: IVideoEntry, invalidateCache = false): Promise<IVideoExtensionData> {
        const options = getOptionsQueryParams({ minsToLive: 60, invalidateCache, VideoEventId: videoEntry.Id, ExternalId: videoEntry.ExternalId });

        const result = await lastValueFrom(this.httpClient
            .get<IVideoExtensionData>(`/api/cameras/entries/extensiondata`, options));

        const resultGForce = this.unitConversionService.convertExtensionDataIntoGForce(videoEntry, result);

        return resultGForce;
    }

    async getVideosEntriesWithMappings(startTime: Date, endTime: Date, options?: IGetVideoEntryOptions): Promise<IVideoEntry[]> {
        const [videos, channelMedia, vehicles, drivers, driverAssignments, currentUser, users] = await Promise.all([
            this.getAllVideosEntries(startTime, endTime, options),
            this.getAllChannelVideos(startTime, endTime, true),
            this.vehicleService.getVehicles({ includePeripheralMappings: true, includeAllVehicles: true }) as Promise<IVehicle[]>,
            this.driverService.getDrivers() as Promise<IDriver[]>,
            this.driverAssignmentService.getDriverAssignments(false) as Promise<IVehicleDriverMapping[]>,
            this.currentUserService.getCurrentUser() as Promise<IUser>,
            this.usersService.getUsers({ includeAdmins: true }) as Promise<IUser[]>
        ]);
        return this.mapVideoEntries(videos, channelMedia, vehicles, drivers, driverAssignments, currentUser, users);
    }

    async getVideoThumbnails(videoEntryId: number): Promise<IVideoThumbnail[]> {
        const options = getOptionsQueryParams({ minsToLive: 60 });

        const result = await lastValueFrom(this.httpClient
            .get<IVideoThumbnail[]>(`/api/camera/thumbnails/${videoEntryId}`, options));

        return result.map(thumbnail => {
            const thumbnailUrl = this.getMediaURL(thumbnail.FileGuid);
            return { ...thumbnail, thumbnailUrl };
        })
    }

    async getVideoEntriesRequestedWithMappings(options: IGetVideoEntryOptions = {}): Promise<IVideoEntry[]> {
        if (!options?.startTime || !options?.endTime) {
            const now = new Date();

            options.endTime = now;
            options.startTime = this.dateService.addOrSubtractTime(now, -1, DateUnit.Month);
        }

        const [videos, channelMedia, vehicles, drivers, driverAssignments, currentUser, users] = await Promise.all([
            this.getVideoEntriesRequested(options),
            this.getAllChannelVideos(options?.startTime, options?.endTime, options.invalidateCache, true),
            this.vehicleService.getVehicles({ includePeripheralMappings: true }) as Promise<IVehicle[]>,
            this.driverService.getDrivers() as Promise<IDriver[]>,
            this.driverAssignmentService.getDriverAssignments(false) as Promise<IVehicleDriverMapping[]>,
            this.currentUserService.getCurrentUser() as Promise<IUser>,
            this.usersService.getUsers({ includeAdmins: true }) as Promise<IUser[]>
        ]);

        return this.mapVideoEntries(videos, channelMedia, vehicles, drivers, driverAssignments, currentUser, users);
    }

    async getVideosEntriesForVehicle(vehicleId: number, startTime: Date, endTime: Date, options?: IGetVideoEntryOptions): Promise<IVideoEntry[]> {
        const videos = await this.getAllVideosEntriesFromDatabase(startTime, endTime);

        let videosForDisplay = videos
            .filter(v => v.VehicleId === vehicleId)
            .map(v => this.getVideoEntryForDisplay(v));

        if (options?.processEntries || options?.createFileUrls) {
            videosForDisplay = this.getVideoEntriesWithFileUrls(videosForDisplay);
        }


        return this.getVideosFiltered(videosForDisplay, options?.vehicleFilter, options?.driverFilter, options?.groupFilter);
    }

    async getVideoEntryByProductGroupIdAndExternalId(productGroupId: number, videoExternalId: string): Promise<IVideoEntry> {
        return await lastValueFrom(this.httpClient
            .get<IVideoEntry>(`/api/cameras/entry/${productGroupId}/${videoExternalId}`));
    }

    async getVideoEntry(entryId: number, invalidateCache = false): Promise<IVideoEntry> {
        const options = getOptionsQueryParams({ minsToLive: 60, invalidateCache });

        let videoEntry = await lastValueFrom(this.httpClient
            .get<IVideoEntry>(`/api/cameras/entry/${entryId}`, options));

        videoEntry.driver = await this.driverAssignmentService.getDriverAssignmentForTime(videoEntry.VehicleId, videoEntry.VideoStartTime);
        videoEntry.vehicle = await this.vehicleService.getVehicle(videoEntry.VehicleId, { includePeripheralMappings: true });

        videoEntry = this.getVideoEntryForDisplay(videoEntry);

        return videoEntry;
    }

    async updateVideoEntry(videoEntry: IVideoEntry): Promise<IVideoEntry> {
        const videoEntryCopy: IVideoEntry = {
            ...videoEntry,
            vehicle: null,
            driver: null,
            eventCategory: null,
            markerModel: null
        };

        const apiResult = await lastValueFrom(this.httpClient.post<IVideoEntry>("/api/cameras", videoEntryCopy));
        const result = { ...videoEntry, ...apiResult };

        this.updateVideoInAllCaches(result); // Update all caches

        this.downloadVideoCountUpdatedSubject.next();
        this.videoUpdateSubject.next(result);

        return result;
    }

    async getVideoEntriesRequested(options?: IGetVideoEntryOptions): Promise<IVideoEntry[]> {
        if (!options?.startTime || !options?.endTime) {
            const now = new Date();

            options.endTime = now;
            options.startTime = this.dateService.addOrSubtractTime(now, -1, DateUnit.Month);
        }

        const videos = await this.getVideoEntriesRequestedFromDatabase(options?.startTime, options?.endTime);

        const videosForDisplay = videos.map(video => this.getVideoEntryForDisplay(video));
        return this.getVideosFiltered(videosForDisplay, options?.vehicleFilter, options?.driverFilter, options?.groupFilter, options?.excludeMediaDeleted);
    }

    async getAllVideosEntries(startTime: Date, endTime: Date, options?: IGetVideoEntryOptions): Promise<IVideoEntry[]> {
        let videos = await this.getAllVideosEntriesFromDatabase(startTime, endTime);

        if (!options?.includeArchived) {
            videos = videos.filter(v => v.Status != VideoStatus.Archived);
        }

        if (options?.downloadedOnly) {
            videos = videos.filter(v => v.Status == VideoStatus.Downloaded || v.Status == VideoStatus.PartiallyDownloaded);
        }

        if (options?.processEntries) {
            videos = videos
                .map(v => this.getVideoEntryForDisplay(v))
                .map(v => this.getVideoWithMetadataFormattedForDisplay(v, null));
        }

        if (options?.processEntries || options?.createFileUrls) {
            videos = this.getVideoEntriesWithFileUrls(videos);
        }

        const setVideoPositionPromises = videos
            .filter(v => v.Longitude == 0 && v.Latitude == 0 && v.Status == VideoStatus.Downloaded)
            .map(v => this.setVideoPosition(v));

        const updatedVideos = await Promise.all(setVideoPositionPromises);

        videos = videos.map(video => {
            const updatedVideo = updatedVideos.find(x => x.Id === video.Id);

            if (updatedVideo) {
                video.Longitude = updatedVideo.Longitude;
                video.Latitude = updatedVideo.Latitude;
            }

            return video;
        });

        return this.getVideosFiltered(videos, options?.vehicleFilter, options?.driverFilter, options?.groupFilter);
    }

    private async getVideoEntriesRequestedFromDatabase(startTime: Date, endTime: Date): Promise<IVideoEntry[]> {
        const options = getOptionsQueryParams({ startTime, endTime });

        return await lastValueFrom(this.httpClient
            .get<IVideoEntry[]>("/api/cameras/entries/requested", options));
    }

    async requestVideoUpload(videoRequest: IVideoRequest): Promise<void> {
        if (!videoRequest.Latitude || !videoRequest.Longitude) {
            videoRequest = await this.getLatLngForVideoRequest(videoRequest);
        }

        await lastValueFrom(this.httpClient.post(`/api/cameras/request`, videoRequest));
        this.downloadVideoCountUpdatedSubject.next();
    }

    async getLatLngForVideoRequest(videoRequest: IVideoRequest): Promise<IVideoRequest> {
        return lastValueFrom(this.plotService.getPlotNearestToTime(videoRequest.VehicleId, videoRequest.VideoStartTime)).then(plot => {
            videoRequest.Latitude = plot?.Latitude;
            videoRequest.Longitude = plot?.Longitude;
            return videoRequest;
        });
    }

    async setVideoPosition(videoEntry: IVideoEntry): Promise<IVideoEntry> {
        return await lastValueFrom(this.httpClient
            .post<IVideoEntry>("/api/cameras/entry/setPosition", videoEntry));
    }

    //--

    private getTimeBasedCacheKey(prefix: string, startTime: Date, endTime: Date): string {
        return `${prefix}-${startTime.toISOString()}-${endTime.toISOString()}`;
    }

    private trackCache(cacheKey: string): void {
        this.activeTimePeriodCaches.add(cacheKey);
    }

    private updateVideoInAllCaches(videoEntry: IVideoEntry): void {
        this.recentlyModifiedVideos.set(videoEntry.Id, videoEntry); // Store video in the map for 60 seconds
        setTimeout(() => {
            this.recentlyModifiedVideos.delete(videoEntry.Id);
        }, 60000); // 60 seconds

        const entryCaches = Array.from(this.activeTimePeriodCaches)
            .filter(key => key.startsWith(this.videosEntriesCachePrefix));

        entryCaches.forEach(cacheName => {
            this.dataCacheService.addOrUpdateItem(
                videoEntry,
                cacheName,
                'Id'
            );
        });
    }

    private mapVideoEntries(
        videos: IVideoEntry[],
        channelMedia: IVideoChannelMedia[],
        vehicles: IVehicle[],
        drivers: IDriver[],
        driverAssignments: IVehicleDriverMapping[],
        currentUser: IUser,
        users: IUser[]
    ): IVideoEntry[] {
        const videosWithLatestStatus = this.getVideoWithLatestStatus(videos);

        const result = videosWithLatestStatus.map(video => {
            if (!video.channelMedia) {
                video.channelMedia = [];
            }

            video.channelMedia.push(...channelMedia.filter(m => m.VideoEntryId === video.Id));

            video.channelMedia = this.removeDuplicateChannels(video.channelMedia);

            if (video.Status == VideoStatus.Downloaded && !video.Mp4VideoFileGuid && video.channelMedia.any()) {
                video.channelMedia = video.channelMedia.filter(media => !!media.Mp4VideoFileGuid);

                if (video.channelMedia.length) {
                    video.Mp4VideoFileGuid = video.channelMedia[0].Mp4VideoFileGuid;
                    video.ThumbnailFileGuid = video.channelMedia[0].ThumbnailFileGuid;
                    video.StoryboardFileGuid = video.channelMedia[0].StoryboardFileGuid;
                    video.WebmVideoFileGuid = video.channelMedia[0].WebmVideoFileGuid;
                    video.channelMedia[0].mainChannel = true;
                }
            }

            video.displayEventCode = this.getEventLabel(video);

            if (video.VehicleId) {
                video.vehicle = this.vehicleService.findVehicle(vehicles, video.VehicleId);

                if (video.vehicle?.TagIds) {
                    video.tagIds = [];
                    video.tagIds = video.tagIds?.concat(video.vehicle.TagIds);
                }
                const assignmentAtTimeOfVideo = driverAssignments.find(assignment => {
                    return assignment.VehicleId === video.VehicleId &&
                        this.dateService.dateIsBetweenDates(video.EventTimeUtc, String(assignment.StartUtc), String(assignment.EndUtc))
                });

                if (assignmentAtTimeOfVideo) {
                    video.driver = drivers.find(driver => driver.Id === assignmentAtTimeOfVideo.DriverId);
                }
            }

            if (video.DriverId && !video.driver) {
                video.driver = this.driverService.findDriver(drivers, video.DriverId);
            }

            if (video.RequestedBy) {
                video.requestedByUser = this.usersService.findUser(users, video.RequestedBy);
            }

            video = this.getVideoWithMetadataFormattedForDisplay(video, currentUser);

            return video;
        });

        result.sort((a, b) => this.dateService.sort(a.EventTimeUtc, b.EventTimeUtc));

        return this.getVideoEntriesWithFileUrls(result);
    }

    private removeDuplicateChannels(channelMedia: IVideoChannelMedia[]): IVideoChannelMedia[] {
        const uniqueChannels: IVideoChannelMedia[] = [];

        for (const media of channelMedia) {
            let unique = true;

            for (const compare of uniqueChannels) {
                if (compare.ChannelId === media.ChannelId) {
                    unique = false;
                    break;
                }
            }

            if (unique) {
                uniqueChannels.push(media);
            }
        }

        return uniqueChannels;
    }

    private async getAllVideosEntriesFromDatabase(startTime: Date, endTime: Date): Promise<IVideoEntry[]> {
        const options = getOptionsQueryParams({ startTime, endTime });

        return await lastValueFrom(this.httpClient
            .get<IVideoEntry[]>("/api/cameras/entries", options));
    }

    private getVideoEntryForDisplay(videoEntry: IVideoEntry): IVideoEntry {
        const displayEventCode = this.getEventLabel(videoEntry);
        const displayStatus = this.cameraStatusService.getVideoDisplayStatus(videoEntry);
        const placeName = videoEntry.PlaceName || this.translationService.t("cameraService.locationUnavailable");

        const channelMedia = this.getVideoChannelMediaForDisplay([{
            ChannelId: 0, // Aka primary
            thumbnailUrl: videoEntry.thumbnailUrl,
            ThumbnailFileGuid: videoEntry.ThumbnailFileGuid,
            mp4VideoUrl: videoEntry.mp4VideoUrl,
            Mp4VideoFileGuid: videoEntry.Mp4VideoFileGuid,
            mainChannel: true
        }]);

        const displayEventType = videoEntry.Mp4VideoFileGuid
            ? this.translationService.t("cameraService.video")
            : this.translationService.t("cameraService.event");

        const ms = this.dateService.getDiff(videoEntry.VideoStartTime, videoEntry.VideoEndTime, DateUnit.Ms);
        const duration = ms === 0
            ? "10s"
            : Math.round(ms / 1000) + "s";

        const videoEntryForDisplay: IVideoEntry = {
            ...videoEntry,
            displayEventCode,
            displayStatus,
            PlaceName: placeName,
            channelMedia,
            displayEventType,
            duration,
            MetaData: {
                ...videoEntry.MetaData,
                DownloadProgress: videoEntry.Status == VideoStatus.Downloaded ? 100 : videoEntry.MetaData.DownloadProgress
            },
            VideoExtensionData: this.unitConversionService.convertExtensionDataIntoGForce(videoEntry, videoEntry.VideoExtensionData)
        }

        return videoEntryForDisplay;
    }

    private getVideoEntriesWithFileUrls(videoEntries: IVideoEntry[]): IVideoEntry[] {
        return videoEntries.map(videoEntry => {
            if (videoEntry.ThumbnailFileGuid) {
                videoEntry.thumbnailUrl = this.getMediaURL(videoEntry.ThumbnailFileGuid);
            }

            if (videoEntry.Mp4VideoFileGuid) {
                videoEntry.mp4VideoUrl = this.getMediaURL(videoEntry.Mp4VideoFileGuid);
            }

            if (videoEntry.StoryboardFileGuid) {
                videoEntry.storyboardUrl = this.getMediaURL(videoEntry.StoryboardFileGuid);
            }

            return videoEntry;
        })
    }

    private getEventLabel(videoEvent: IVideoEntry): string {
        if (videoEvent.MetaData.Label &&
            videoEvent.EventCode !== VideoEventCode.Button &&
            videoEvent.EventCode !== VideoEventCode.Sensor) {
            return videoEvent.MetaData.Label.trim();
        }

        return videoEvent.EventCode != null
            ? this.translationService.translateEnum(VideoEventCode, "VideoEventCode", videoEvent.EventCode)
            : this.translationService.translateEnum(VideoEventCode, "VideoEventCode", VideoEventCode.Unknown);
    }

    private getVideosFiltered(
        videos: IVideoEntry[],
        vehicleFilter: IVehicleFilter,
        driverFilter: IDriverFilter,
        groupFilter: IGroupFilter,
        excludeMediaDeleted?: boolean
    ): IVideoEntry[] {
        videos = videos.filter(video => {
            if (!video.MetaData || !video.MetaData.Label) {
                return true;
            }

            const labelLower = video.MetaData.Label.toLowerCase();

            if (labelLower.indexOf("sensor") >= 0) {
                return false;
            }

            if (labelLower === "left" || labelLower === "right" || labelLower === "reverse") {
                return false;
            }

            return true;
        });

        if (excludeMediaDeleted) {
            videos = videos.filter(video => video.Status !== VideoStatus.MediaDeleted);
        }

        if ((!vehicleFilter || !vehicleFilter.vehicle) && (!driverFilter || !driverFilter.driver) && (!groupFilter || !groupFilter.group)) {
            return videos;
        }

        if (vehicleFilter && vehicleFilter.vehicle) {
            videos = videos.filter(v => v.VehicleId === vehicleFilter.vehicle.Id);
        }

        if (driverFilter && driverFilter.driver) {
            videos = videos.filter(v => v.DriverId === driverFilter.driver.Id);
        }

        if (groupFilter && groupFilter.group) {
            videos = this.filterVideoByGroup(videos, groupFilter);
        }

        return videos;
    }

    private getVideoChannelMediaForDisplay(channelMedia: IVideoChannelMedia[]): IVideoChannelMedia[] {
        return channelMedia.map(media => {
            if (media.ThumbnailFileGuid) {
                media.thumbnailUrl = this.getMediaURL(media.ThumbnailFileGuid);
            }

            if (media.Mp4VideoFileGuid) {
                media.mp4VideoUrl = this.getMediaURL(media.Mp4VideoFileGuid);
            }

            return media;
        });
    }

    private filterVideoByGroup(videosToFilter: IVideoEntry[], filter: IGroupFilter): IVideoEntry[] {
        const filteredVideos: IVideoEntry[] = [];

        videosToFilter.forEach(v => {
            const alreadyAdded = filteredVideos.some(fv => fv.Id === v.Id);

            if (!alreadyAdded) {
                const driverIncluded = filter.group.DriverIds.includes(v.DriverId);
                const vehicleIncluded = filter.group.VehicleIds.includes(v.VehicleId);

                if (driverIncluded || vehicleIncluded) {
                    filteredVideos.push(v);
                }
            }
        });

        return filteredVideos;
    }

    private getMediaURL(guid: string, uriRedirect = false): string {
        let url = `${this.apiUrl}/api/cameras/media/${guid}`;

        if (uriRedirect) {
            url += "/uri/redirect";
        }

        return url;
    }

    private getVideoWithMetadataFormattedForDisplay(videoEntry: IVideoEntry, currentUser: IUser): IVideoEntry {
        if (!videoEntry.MetaData) {
            return videoEntry;
        }

        const watched = this.videoSeen(videoEntry);
        const lastNote = this.getVideoLastNote(videoEntry);
        const reviewStatus = lastNote.Status;
        const reviewSubStatus = lastNote.ReviewStatus;
        const coachingStatus = lastNote.CoachingStatus;
        const reviewStatusDisplayValue = this.videoReviewStatusService.getVideoReviewStatusDisplayValue(videoEntry);

        const formattedVideo: IVideoEntry = {
            ...videoEntry,
            watched,
            reviewStatus,
            reviewSubStatus,
            coachingStatus,
            reviewStatusDisplayValue
        }

        if (currentUser != null) {
            formattedVideo.reviewed = videoEntry.MetaData.Authorised.some(userAction => currentUser.Id === userAction.UserId);
            formattedVideo.favourite = videoEntry.MetaData.Favourite.some(f => f.UserId === currentUser.Id);
            formattedVideo.companyFavourite = videoEntry.MetaData.Favourite.length > 0;
        }

        formattedVideo.MetaData.VideoObservations.sort((a, b) => {
            if (VideoObservation[a] < VideoObservation[b]) {
                return -1;
            }

            if (VideoObservation[a] > VideoObservation[b]) {
                return 1;
            }

            return 0;
        });

        return formattedVideo;
    }

    private videoSeen(v: IVideoEntry): boolean {
        return v.Status == VideoStatus.Downloaded && v.MetaData && v.MetaData.Watched.length > 0;
    }

    private getVideoLastNote(v: IVideoEntry): IUserAction {
        if (!v.MetaData || !v.MetaData.Notes || v.MetaData.Notes.length === 0) {
            return {
                Status: VideoReviewStatus.NeedsReview,
                ReviewStatus: VideoReviewSubStatus.None,
                CoachingStatus: VideoCoachingStatus.None,
            };
        }

        return v.MetaData.Notes[v.MetaData.Notes.length - 1];
    }

    private async getAllChannelVideos(
        startTime: Date,
        endTime: Date,
        invalidateCache: boolean = false,
        useRequestedTime: boolean = false
    ): Promise<IVideoChannelMedia[]> {
        const channels = await this.getAllChannelVideosFromDatabase(startTime, endTime, useRequestedTime);

        return this.getVideoChannelMediaForDisplay(channels);
    }

    private async getAllChannelVideosFromDatabase(startTime: Date, endTime: Date, useRequestedTime: boolean = false): Promise<IVideoChannelMedia[]> {
        const options = getOptionsQueryParams({ startTime, endTime, useRequestedTime });

        return await lastValueFrom(this.httpClient
            .get<IVideoChannelMedia[]>("/api/cameras/channels/videos", options));
    }
}
